import {
  Button,
  ComboBox,
  InlineNotification,
  NumberInput,
  RadioButton,
  RadioButtonGroup,
  Stack,
  StructuredListBody,
  StructuredListCell,
  StructuredListHead,
  StructuredListRow,
  StructuredListWrapper,
  TextArea,
  TextInput,
} from '@carbon/react';
import { asProblem, hasPermission, PageHeader, problemMessage, useAdmin } from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation } from '@pos/i18n';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { accountNumbers, usePromotions, useVoucherBatches } from './rewardsApi';

type Kind = 'coupon' | 'gift_voucher';
type Choice = { id: string; text: string };
type Result = HoComponents['schemas']['IssueRewardListResult'];

/**
 * Issuing the same coupon or gift voucher to a list of members by account number (ADR 0079, FR-MEM-09), up to 1,000 at a time.
 * Sending the same list again (a retry) issues nothing twice; members who couldn't get it are listed with the reason.
 */
export function IssueRewardsPage() {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const canIssue = hasPermission(session, 'ho.members.accounts.manage');
  const [kind, setKind] = useState<Kind>('coupon');
  const [promotion, setPromotion] = useState<Choice | null>(null);
  const [batch, setBatch] = useState<Choice | null>(null);
  const [validDays, setValidDays] = useState<number | null>(30);
  const [note, setNote] = useState('');
  const [list, setList] = useState('');
  const [key, setKey] = useState(() => crypto.randomUUID());
  const [promotionSearch, setPromotionSearch] = useState('');
  const [batchSearch, setBatchSearch] = useState('');
  const promotions = usePromotions(promotionSearch);
  const batches = useVoucherBatches(batchSearch);
  const numbers = accountNumbers(list);

  const issue = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.POST('/api/v1/membership/rewards/issue-list', {
        body: {
          accountNos: numbers,
          kind,
          promotionId: kind === 'coupon' ? (promotion?.id ?? null) : null,
          voucherBatchId: kind === 'gift_voucher' ? (batch?.id ?? null) : null,
          validDays,
          note: note.trim() || null,
          idempotencyKey: key,
        },
      });
      if (!data) throw error;
      return data;
    },
  });

  const valid = canIssue && numbers.length > 0 && numbers.length <= 1000 && (kind === 'coupon' ? !!promotion : !!batch) && (validDays === null || validDays >= 1);
  const result: Result | undefined = issue.data;

  return (
    <section className="pos-admin-page">
      <PageHeader title={t('loyalty.issueList.title')} description={t('loyalty.issueList.hint')} />
      <Stack gap={6} className="pos-issue-rewards">
        <RadioButtonGroup legendText={t('loyalty.rewards.kind')} name="issue-kind" valueSelected={kind} onChange={(v) => setKind(v as Kind)}>
          <RadioButton id="issue-kind-coupon" labelText={t('loyalty.rewards.coupon')} value="coupon" />
          <RadioButton id="issue-kind-voucher" labelText={t('loyalty.rewards.giftVoucher')} value="gift_voucher" />
        </RadioButtonGroup>
        {kind === 'coupon' ? (
          <ComboBox<Choice>
            id="issue-promotion"
            titleText={t('loyalty.rewards.promotion')}
            helperText={t('loyalty.rewards.promotionHelp')}
            items={promotions.data ?? []}
            itemToString={(item) => item?.text ?? ''}
            selectedItem={promotion}
            onInputChange={setPromotionSearch}
            onChange={({ selectedItem }) => setPromotion(selectedItem ?? null)}
          />
        ) : (
          <ComboBox<Choice>
            id="issue-batch"
            titleText={t('loyalty.rewards.batch')}
            helperText={t('loyalty.issueList.batchHelp')}
            items={batches.data ?? []}
            itemToString={(item) => item?.text ?? ''}
            selectedItem={batch}
            onInputChange={setBatchSearch}
            onChange={({ selectedItem }) => setBatch(selectedItem ?? null)}
          />
        )}
        <NumberInput
          id="issue-valid-days"
          label={t('loyalty.rewards.validDays')}
          helperText={t('loyalty.rewards.validDaysHelp')}
          value={validDays ?? ''}
          min={1}
          max={3660}
          step={1}
          allowEmpty
          onChange={(_, { value }) => setValidDays(value === '' || value === undefined ? null : Number(value) || null)}
        />
        <TextInput id="issue-note" labelText={t('loyalty.rewards.note')} value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />
        <TextArea
          id="issue-accounts"
          labelText={t('loyalty.issueList.accounts')}
          helperText={t('loyalty.issueList.accountsHelp', { count: numbers.length })}
          invalid={numbers.length > 1000}
          invalidText={t('loyalty.issueList.tooMany')}
          rows={8}
          value={list}
          onChange={(e) => setList(e.target.value)}
        />
        <div>
          <Button disabled={!valid || issue.isPending} onClick={() => issue.mutate()}>
            {t('loyalty.issueList.issue', { count: numbers.length })}
          </Button>
        </div>
        {issue.error && <InlineNotification kind="error" role="alert" lowContrast hideCloseButton title={problemMessage(t, asProblem(issue.error))} />}
        {result && (
          <Stack gap={5}>
            <InlineNotification kind="success" role="status" lowContrast hideCloseButton title={t('loyalty.issueList.issued', { count: Number(result.issued) })} />
            {result.refused.length > 0 && (
              <StructuredListWrapper isCondensed aria-label={t('loyalty.issueList.refused')}>
                <StructuredListHead>
                  <StructuredListRow head>
                    <StructuredListCell head>{t('loyalty.issueList.accountNo')}</StructuredListCell>
                    <StructuredListCell head>{t('loyalty.issueList.reason')}</StructuredListCell>
                  </StructuredListRow>
                </StructuredListHead>
                <StructuredListBody>
                  {result.refused.map((r) => (
                    <StructuredListRow key={r.accountNo}>
                      <StructuredListCell>{r.accountNo}</StructuredListCell>
                      <StructuredListCell>{problemMessage(t, { code: r.code, title: r.code })}</StructuredListCell>
                    </StructuredListRow>
                  ))}
                </StructuredListBody>
              </StructuredListWrapper>
            )}
            <div>
              <Button
                kind="tertiary"
                onClick={() => {
                  issue.reset();
                  setList('');
                  setKey(crypto.randomUUID());
                }}
              >
                {t('loyalty.issueList.another')}
              </Button>
            </div>
          </Stack>
        )}
      </Stack>
    </section>
  );
}
